import React, { useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/Alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/Progress';
import { Button } from '@/components/ui/button';
import { Rocket, Database, Wifi, AlertTriangle, Info, CheckCircle2, AlertCircle } from 'lucide-react';

export function FeedbackComponents() {
  const [showDangerDialog, setShowDangerDialog] = useState(false);
  const [progressValue, setProgressValue] = useState(65);

  return (
    <AppShell>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-semibold text-foreground mb-2">
            Feedback Components
          </h1>
          <p className="text-muted-foreground">
            Alert, AlertDialog, Progress, and feedback components
          </p>
        </div>

        {/* Alerts */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Alerts
          </h3>

          <div className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Information</AlertTitle>
              <AlertDescription>
                This is an informational alert. It provides helpful context or tips.
              </AlertDescription>
            </Alert>

            <Alert variant="default" className="border-green-500/50 text-green-900 dark:text-green-100">
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>
                Your changes have been saved successfully!
              </AlertDescription>
            </Alert>

            <Alert variant="default" className="border-yellow-500/50 text-yellow-900 dark:text-yellow-100">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Warning</AlertTitle>
              <AlertDescription>
                This action may affect your system performance. Please review before proceeding.
              </AlertDescription>
            </Alert>

            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>
                Failed to connect to the database. Please check your connection and try again.
              </AlertDescription>
            </Alert>

            <Alert>
              <Rocket className="h-4 w-4" />
              <AlertDescription>
                Custom icon alert: Your deployment is ready to launch!
              </AlertDescription>
            </Alert>
          </div>
        </Card>

        {/* Alert Dialogs */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Confirmation Dialogs
          </h3>

          <div className="flex flex-wrap gap-3">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Danger Confirmation</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Flow</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this flow? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Warning Confirmation</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Restart Modem</AlertDialogTitle>
                  <AlertDialogDescription>
                    Restarting this modem will interrupt all active connections. Continue?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction>Restart</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Info Confirmation</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Save Changes</AlertDialogTitle>
                  <AlertDialogDescription>
                    Would you like to save your changes before exiting?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Discard</AlertDialogCancel>
                  <AlertDialogAction>Save</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </Card>

        {/* Progress Bars */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-foreground mb-6">
            Progress Bars
          </h3>

          <div className="space-y-8">
            {/* Controlled Progress */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-4">
                Controlled Progress
              </h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Small</span>
                    <span className="text-sm text-muted-foreground">{progressValue}%</span>
                  </div>
                  <Progress value={progressValue} className="h-2" />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Medium (Default)</span>
                    <span className="text-sm text-muted-foreground">{progressValue}%</span>
                  </div>
                  <Progress value={progressValue} />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Large</span>
                    <span className="text-sm text-muted-foreground">{progressValue}%</span>
                  </div>
                  <Progress value={progressValue} className="h-4" />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProgressValue(Math.max(0, progressValue - 10))}
                  >
                    -10%
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProgressValue(Math.min(100, progressValue + 10))}
                  >
                    +10%
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setProgressValue(0)}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </div>

            {/* Color Variants */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-4">
                Different Values
              </h4>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">75% Complete</span>
                    <span className="text-sm text-muted-foreground">75%</span>
                  </div>
                  <Progress value={75} />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">85% Complete</span>
                    <span className="text-sm text-muted-foreground">85%</span>
                  </div>
                  <Progress value={85} className="[&>div]:bg-green-500" />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">60% Complete</span>
                    <span className="text-sm text-muted-foreground">60%</span>
                  </div>
                  <Progress value={60} className="[&>div]:bg-yellow-500" />
                </div>

                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-muted-foreground">30% Complete</span>
                    <span className="text-sm text-muted-foreground">30%</span>
                  </div>
                  <Progress value={30} className="[&>div]:bg-red-500" />
                </div>
              </div>
            </div>

            {/* Real-world Examples */}
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-4">
                Real-world Examples
              </h4>
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-md border">
                  <div className="flex items-center gap-3 mb-3">
                    <Database className="w-5 h-5 text-blue-500" />
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-foreground">
                        Database Migration
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        Migrating 15,234 records...
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs text-muted-foreground">42% complete</span>
                  </div>
                  <Progress value={42} />
                </div>

                <div className="p-4 bg-muted rounded-md border">
                  <div className="flex items-center gap-3 mb-3">
                    <Wifi className="w-5 h-5 text-green-500" />
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-foreground">
                        Network Sync
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        Syncing modem configurations...
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs text-muted-foreground">92% complete</span>
                  </div>
                  <Progress value={92} className="[&>div]:bg-green-500" />
                </div>

                <div className="p-4 bg-muted rounded-md border">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    <div className="flex-1">
                      <h5 className="text-sm font-medium text-foreground">
                        System Update
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        Installing critical updates...
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs text-muted-foreground">68% complete</span>
                  </div>
                  <Progress value={68} className="[&>div]:bg-yellow-500" />
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}
